import React, { FC, useContext, useState, useEffect } from "react"
import clsx from "clsx"
import {
  ContentfulHeading,
  ContentfulCurrencyList,
  ContentfulSection,
} from "../../types/graphql-types"
import CurrencySelectorElement from "./CurrencySelectorElement"
import { PriceContext, CurrencyName, PriceContextType } from "../context/priceContext"
import BaseModal, { ModalStatus } from "./rootModal"
import { breakpoints as bp, TripleColumnContainer } from "./layout"
import colors from "../utils/colors"
import styled from "styled-components"
import Button from "./forms/button"
import Icon from "./Icon"

interface Props {
  contents: ContentfulSection
}

const arrowDownWidth = "24px"

const ColumnContainer = styled(TripleColumnContainer)`
  padding: 0 !important;
  width: 85%;
  margin-top: 33px;
  .isSelected {
    :hover {
      cursor: initial;
    }
    p {
      color: ${colors.teal};
    }
  }
  @media (min-width: ${bp.tabletMin}) {
    margin-top: 96px;
  }

  @media (min-width: ${bp.desktopMin}) {
    width: 80%;
  }
`
const NonDesktopButton = styled(Button)`
  border: none;
  text-align: left;
  font-weight: 500;
  padding: 0 calc(1rem + ${arrowDownWidth}) 0 1rem;
  position: relative;
  white-space: nowrap;
  &:hover {
    background: none;
  }
  svg {
    position: absolute;
    right: 8px;
  }
  @media (min-width: ${bp.tabletMin}) {
    width: 100%;
    svg {
      margin-left: 1rem;
    }
  }
  @media (min-width: ${bp.desktopMin}) {
    display: none;
  }
`
const DesktopButton = styled(Button)`
  display: flex;
  line-height: 20px;
  align-items: center;
  font-size: 18px;
  height: 40px;
  padding: 0 20px;
  margin-right: 1rem;
  white-space: nowrap;
  svg {
    margin-left: 8px;
  }
  @media (max-width: ${bp.tabletMax}) {
    display: none;
  }
`

const CurrencySelector: FC<Props> = props => {
  const { contents } = props

  const { currencyName, setCurrencyName } = useContext(PriceContext) as PriceContextType
  const [isCurrencyModalOpen, setCurrencyModalOpen] = useState(false)
  const [currencySymbol, setCurrencySymbol] = useState<string | undefined>(undefined)
  const heading = (contents?.heading as ContentfulHeading).heading!
  const sectionFields = contents?.contentfulfields || []

  const getSelectedCurrencySymbol = (str: CurrencyName): string => {
    const res = (sectionFields as ContentfulCurrencyList[]).filter(
      item => item.currencyName === str
    )[0].currencySymbol!
    return res
  }

  useEffect(() => {
    const userSelectedCurrency = localStorage.getItem("UserSelectedCurrency") as CurrencyName
    if (userSelectedCurrency) {
      setCurrencyName(userSelectedCurrency)
    }
  }, [])

  useEffect(() => {
    setCurrencySymbol(getSelectedCurrencySymbol(currencyName))
  }, [currencyName])

  return (
    <>
      <BaseModal
        modalStatus={ModalStatus.CLOSE}
        full
        showToggleIcon
        isOpen={isCurrencyModalOpen}
        onClose={() => {
          setCurrencyModalOpen(false)
        }}
        header={heading}
      >
        <ColumnContainer>
          {(sectionFields as ContentfulCurrencyList[]).map((item, index) => (
            <CurrencySelectorElement
              classNames={clsx(item.currencyName === currencyName ? "isSelected" : "")}
              key={index}
              currencyHeading={item.currencyHeading!}
              currencyName={item.currencyName!}
              currencySymbol={item.currencySymbol!}
              ifShowCheckIcon={item.currencyName === currencyName}
              onClick={() => {
                setCurrencyName(item.currencyName! as CurrencyName)
                localStorage.setItem("UserSelectedCurrency", item.currencyName!)
                setCurrencyModalOpen(false)
              }}
            />
          ))}
        </ColumnContainer>
      </BaseModal>
      <NonDesktopButton
        type="button"
        transparent
        onClick={() => {
          setCurrencyModalOpen(true)
        }}
        data-gtm-cta="Currency-selector"
      >
        {
          (sectionFields as ContentfulCurrencyList[]).find(
            item => item.currencyName === currencyName
          )?.currencyHeading
        }{" "}
        {currencySymbol}
        <Icon icon="arrow-down" width={arrowDownWidth} height={arrowDownWidth} />
      </NonDesktopButton>
      <DesktopButton
        type="button"
        transparent
        onClick={() => {
          setCurrencyModalOpen(true)
        }}
        data-gtm-cta="Currency-selector"
      >
        {currencyName}
        <Icon icon="arrow-down" width={arrowDownWidth} height={arrowDownWidth} />
      </DesktopButton>
    </>
  )
}

export default CurrencySelector
